// iter155 — Rice Running Report
//
// Four stacked views over the same rice ledger:
//   • Daily — one row per date in month, with IN (dated receipts), OUT
//     (dayKind=working rice consumption via buildDayBreakdown), running
//     closing. Legacy single-line receipts show a "Split into real dates"
//     chip linking to Fund Management.
//   • Weekly — ISO weeks of the selected month.
//   • Monthly — rolling 12 months ending at selected month.
//   • Yearly — financial-year totals grouped from the monthly rows.
//
// Export: XLSX via exceljs (sheet per view) + Print PDF.
// Reconciles against cascadeClosing(prevMonth) so Fund Management closing
// equals this report's opening of next month, every time.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileSpreadsheet, Printer, Sparkles, ScanLine, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useActiveSchool } from "@/lib/activeSchool";
import { MONTH_OPTIONS } from "@/lib/mdcf";
import { buildDailyLedger, buildMonthlyLedger, aggregate } from "@/lib/riceLedger";

const kg3 = (v) => Number(v || 0).toFixed(3);
function dmy(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

export default function RiceReport() {
  const { active } = useActiveSchool();
  const schoolId = active?.id || "";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [view, setView] = useState("daily"); // daily | weekly | monthly | yearly
  const [ledger, setLedger] = useState({ opening: 0, rows: [], totals: { in: 0, out: 0, closing: 0 }, receipts: [], hasLegacyLumpSum: false });
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const printAreaRef = useRef(null);

  const yearChoices = useMemo(() => {
    const cy = now.getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load daily ledger (used by Daily + Weekly) + monthly 12-month history (used by Monthly + Yearly).
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const ld = await buildDailyLedger(schoolId, year, month);
        if (!cancelled) setLedger(ld);
        // Build rolling 12-month window ending at (year, month)
        let fy = year, fm = month - 11;
        while (fm < 1) { fm += 12; fy -= 1; }
        const monthly = await buildMonthlyLedger(schoolId, fy, fm, year, month);
        if (!cancelled) setMonthlyRows(monthly);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("rice-report load fail", err);
        if (!cancelled) toast.error("Could not load rice ledger");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  const weeklyRows = useMemo(() => aggregate(ledger.rows, "week"), [ledger.rows]);
  const yearlyRows = useMemo(() => {
    const map = new Map();
    monthlyRows.forEach((r) => {
      const iso = `${r.year}-${String(r.month).padStart(2, "0")}-01`;
      const fyKey = (parseInt(iso.slice(5, 7), 10) >= 4)
        ? `${iso.slice(0, 4)}-${String((parseInt(iso.slice(0, 4), 10) + 1) % 100).padStart(2, "0")}`
        : `${parseInt(iso.slice(0, 4), 10) - 1}-${String(parseInt(iso.slice(0, 4), 10) % 100).padStart(2, "0")}`;
      const cur = map.get(fyKey) || { fy: fyKey, in: 0, out: 0, firstOpening: r.opening, lastClosing: r.closing };
      cur.in += r.in; cur.out += r.out; cur.lastClosing = r.closing;
      map.set(fyKey, cur);
    });
    return [...map.values()];
  }, [monthlyRows]);

  const handlePrint = () => {
    document.body.classList.add("rice-print-active");
    requestAnimationFrame(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("rice-print-active"), 200);
    });
  };

  // iter155 Part C — Vector PDF export via jsPDF + jspdf-autotable. Writes
  // real text + table primitives instead of the raster html2canvas path, so
  // the PDF stays crisp at any zoom AND the file is 10-20x smaller.
  const handleVectorPdf = async () => {
    try {
      const [{ jsPDF }, autoTableMod] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableMod.default || autoTableMod.autoTable;
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const mName = MONTH_OPTIONS.find((m) => m.v === month)?.en || month;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(`${active?.schoolName || "School"} — Rice Running Report`, 40, 42);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`UDISE: ${active?.udiseCode || "—"}    Month: ${mName} ${year}`, 40, 60);
      doc.text(`Opening (kg): ${kg3(ledger.opening)}    Total In: ${kg3(ledger.totals.in)}    Total Out: ${kg3(ledger.totals.out)}    Closing: ${kg3(ledger.totals.closing)}`, 40, 76);

      const commonTableOpts = {
        startY: 92,
        theme: "grid",
        headStyles: { fillColor: [44, 95, 45], textColor: 255, fontStyle: "bold" },
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
        margin: { left: 40, right: 40 },
      };

      if (view === "daily") {
        autoTable(doc, {
          ...commonTableOpts,
          head: [["Date", "In (kg)", "Receipts", "Consumed (kg)", "of which DR (kg)", "Closing (kg)"]],
          body: ledger.rows.map((r) => [
            dmy(r.date),
            r.in > 0 ? kg3(r.in) : "—",
            r.inReceipts.map((rr) => `${rr.source || "FCI"}${rr.refNo ? ` #${rr.refNo}` : ""}${rr.legacy ? " [legacy]" : ""}: ${kg3(rr.kg)}`).join("; "),
            r.out > 0 ? kg3(r.out) : "—",
            r.drOut > 0 ? kg3(r.drOut) : "—",
            kg3(r.running),
          ]),
          foot: [["TOTAL", kg3(ledger.totals.in), "", kg3(ledger.totals.out), kg3(ledger.totals.drOut || 0), kg3(ledger.totals.closing)]],
          footStyles: { fillColor: [245, 240, 230], textColor: 20, fontStyle: "bold" },
          columnStyles: { 2: { cellWidth: 240 } },
        });
      } else if (view === "weekly") {
        autoTable(doc, {
          ...commonTableOpts,
          head: [["ISO Week", "From", "To", "In (kg)", "Out (kg)", "Closing at end (kg)"]],
          body: weeklyRows.map((r) => [r.key, dmy(r.firstDate), dmy(r.lastDate), kg3(r.in), kg3(r.out), kg3(r.closingAtEnd || 0)]),
        });
      } else if (view === "monthly") {
        autoTable(doc, {
          ...commonTableOpts,
          head: [["Month", "Opening (kg)", "In (kg)", "Out (kg)", "of which DR (kg)", "Closing (kg)", "Legacy"]],
          body: monthlyRows.map((r) => [`${MONTH_OPTIONS.find((m) => m.v === r.month)?.en || r.month} ${r.year}`, kg3(r.opening), kg3(r.in), kg3(r.out), kg3(r.drOut || 0), kg3(r.closing), r.hasLegacy ? "lump-sum" : ""]),
        });
      } else if (view === "yearly") {
        autoTable(doc, {
          ...commonTableOpts,
          head: [["Financial Year", "In (kg)", "Out (kg)", "Closing at FY end (kg)"]],
          body: yearlyRows.map((r) => [r.fy, kg3(r.in), kg3(r.out), kg3(r.lastClosing || 0)]),
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Generated by MDM Sahayak · ${new Date().toLocaleString()}`, 40, doc.internal.pageSize.getHeight() - 20);
      doc.save(`rice-report-${active?.udiseCode || "school"}-${year}-${String(month).padStart(2, "0")}-${view}.pdf`);
      toast.success("Vector PDF ready — crisp at any zoom");
    } catch (err) {
      console.error(err);
      toast.error("Could not build vector PDF");
    }
  };

  const handleXlsx = async () => {
    try {
      const { default: ExcelJS } = await import("exceljs");
      const wb = new ExcelJS.Workbook();
      wb.creator = "MDM Sahayak";
      wb.created = new Date();
      const header = (sheet, cols) => {
        sheet.addRow(cols).eachCell((c) => {
          c.font = { bold: true, color: { argb: "FFFFFFFF" } };
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2C5F2D" } };
          c.alignment = { horizontal: "center", vertical: "middle" };
        });
      };
      // Daily sheet
      const sd = wb.addWorksheet("Daily");
      sd.addRow([`${active?.schoolName || ""} — UDISE ${active?.udiseCode || ""}`]);
      sd.addRow([`Rice Running Report · ${MONTH_OPTIONS.find((m) => m.v === month)?.en || month} ${year}`]);
      sd.addRow([`Opening on ${year}-${String(month).padStart(2, "0")}-01 : ${kg3(ledger.opening)} kg`]);
      sd.addRow([]);
      header(sd, ["Date", "In (kg)", "Receipts", "Out (kg)", "of which DR (kg)", "Closing (kg)"]);
      ledger.rows.forEach((r) => {
        sd.addRow([
          dmy(r.date),
          Number(r.in.toFixed(3)),
          r.inReceipts.map((rr) => `${rr.source || "FCI"}${rr.refNo ? ` #${rr.refNo}` : ""}${rr.legacy ? " [legacy lump]" : ""}: ${kg3(rr.kg)}`).join("; "),
          Number(r.out.toFixed(3)),
          Number(r.drOut.toFixed(3)),
          Number(r.running.toFixed(3)),
        ]);
      });
      sd.addRow(["TOTAL", Number(ledger.totals.in.toFixed(3)), "", Number(ledger.totals.out.toFixed(3)), Number((ledger.totals.drOut || 0).toFixed(3)), Number(ledger.totals.closing.toFixed(3))]).eachCell((c) => {
        c.font = { bold: true };
      });
      sd.columns.forEach((c, i) => { c.width = i === 2 ? 42 : 16; });
      // Weekly
      const sw = wb.addWorksheet("Weekly");
      header(sw, ["ISO Week", "From", "To", "In (kg)", "Out (kg)", "Closing at end"]);
      weeklyRows.forEach((r) => sw.addRow([r.key, dmy(r.firstDate), dmy(r.lastDate), Number(r.in.toFixed(3)), Number(r.out.toFixed(3)), Number((r.closingAtEnd || 0).toFixed(3))]));
      sw.columns.forEach((c) => { c.width = 16; });
      // Monthly
      const sm = wb.addWorksheet("Monthly");
      header(sm, ["Year", "Month", "Opening (kg)", "In (kg)", "Out (kg)", "of which DR (kg)", "Closing (kg)", "Legacy"]);
      monthlyRows.forEach((r) => sm.addRow([r.year, MONTH_OPTIONS.find((m) => m.v === r.month)?.en || r.month, Number(r.opening.toFixed(3)), Number(r.in.toFixed(3)), Number(r.out.toFixed(3)), Number((r.drOut || 0).toFixed(3)), Number(r.closing.toFixed(3)), r.hasLegacy ? "Yes" : ""]));
      sm.columns.forEach((c) => { c.width = 14; });
      // Yearly
      const sy = wb.addWorksheet("Yearly");
      header(sy, ["Financial Year", "In (kg)", "Out (kg)", "Opening (kg)", "Closing (kg)"]);
      yearlyRows.forEach((r) => sy.addRow([r.fy, Number(r.in.toFixed(3)), Number(r.out.toFixed(3)), Number((r.firstOpening || 0).toFixed(3)), Number((r.lastClosing || 0).toFixed(3))]));
      sy.columns.forEach((c) => { c.width = 18; });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rice-report-${active?.udiseCode || "school"}-${year}-${String(month).padStart(2, "0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rice Report XLSX ready");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error("Could not build XLSX");
    }
  };

  return (
    <main className="min-h-screen bg-sand-50 pb-16" data-testid="rice-report-root">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6 lg:px-8">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-ink-900">Rice Running Report</h1>
            <p className="mt-1 text-sm text-ink-700">
              Dated running ledger of rice received vs consumed. Closing balance here matches Fund Management down to 0.001 kg.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-10 w-28" data-testid="rice-year"><SelectValue /></SelectTrigger>
              <SelectContent>{yearChoices.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
            </Select>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-10 w-40" data-testid="rice-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_OPTIONS.map((m) => (<SelectItem key={m.v} value={String(m.v)}>{m.en}</SelectItem>))}</SelectContent>
            </Select>
            <Button variant="outline" onClick={handleXlsx} data-testid="rice-export-xlsx">
              <FileSpreadsheet className="mr-1 h-4 w-4" /> XLSX
            </Button>
            <Button variant="outline" onClick={handleVectorPdf} data-testid="rice-export-pdf">
              <FileDown className="mr-1 h-4 w-4" /> Vector PDF
            </Button>
            <Button variant="outline" onClick={handlePrint} data-testid="rice-print">
              <Printer className="mr-1 h-4 w-4" /> Print
            </Button>
          </div>
        </header>

        <nav className="mb-5 flex gap-1 rounded-full border border-border bg-white p-1 print:hidden" data-testid="rice-view-tabs">
          {[
            { k: "daily", label: "Daily" },
            { k: "weekly", label: "Weekly" },
            { k: "monthly", label: "Monthly (12 mo)" },
            { k: "yearly", label: "Yearly" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setView(t.k)}
              data-testid={`rice-view-${t.k}`}
              className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                view === t.k ? "bg-clay text-white" : "text-ink-700 hover:text-clay"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {ledger.hasLegacyLumpSum && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3" data-testid="rice-legacy-banner">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5" />
              <span>
                This month shows a <b>legacy single-line receipt</b> for the full {kg3(ledger.receivedRiceTotal)} kg total. Split it into actual dated receipts so your daily ledger is accurate.
              </span>
            </div>
            <Link to="/funds">
              <Button size="sm" variant="outline" data-testid="rice-split-cta"><Sparkles className="mr-1 h-4 w-4" />Split in Fund Management</Button>
            </Link>
          </div>
        )}

        <div ref={printAreaRef}>
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="rice-summary">
            <SummaryCard testid="rice-summary-opening" label="Opening (kg)" value={kg3(ledger.opening)} />
            <SummaryCard testid="rice-summary-in" label="Total In" value={kg3(ledger.totals.in)} />
            <SummaryCard testid="rice-summary-out" label="Total Consumed" value={kg3(ledger.totals.out)} />
            <SummaryCard testid="rice-summary-dr" label="of which Dry Ration" value={kg3(ledger.totals.drOut || 0)} />
            <SummaryCard testid="rice-summary-closing" label="Closing" value={kg3(ledger.totals.closing)}
              highlight={ledger.totals.closing < 0 ? "danger" : "ok"}
            />
          </div>

          {loading && <p className="py-8 text-center text-sm text-ink-700" data-testid="rice-loading">Loading rice ledger…</p>}

          {view === "daily" && !loading && <DailyTable rows={ledger.rows} />}
          {view === "weekly" && !loading && <WeeklyTable rows={weeklyRows} />}
          {view === "monthly" && !loading && <MonthlyTable rows={monthlyRows} />}
          {view === "yearly" && !loading && <YearlyTable rows={yearlyRows} />}
        </div>
      </div>
    </main>
  );
}

function SummaryCard({ label, value, highlight, testid }) {
  const colorCls = highlight === "danger"
    ? "border-rose-300 bg-rose-50 text-rose-700"
    : highlight === "ok" ? "border-moss/30 bg-moss/5 text-moss" : "border-border bg-white text-ink-900";
  return (
    <div className={`rounded-xl border-2 p-3 ${colorCls}`} data-testid={testid}>
      <p className="text-[0.7rem] font-semibold uppercase tracking-wider">{label}</p>
      <p className="mt-1 font-mono text-xl font-bold">{value}</p>
    </div>
  );
}

function DailyTable({ rows }) {
  return (
    <section className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="rice-daily-table">
      <table className="w-full text-sm">
        <thead className="bg-sand-100 text-left text-ink-700">
          <tr>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">Date</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">In (kg)</th>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">Receipts</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Consumed (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">of which DR (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Closing (kg)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date} className="border-t border-border hover:bg-sand-50" data-testid={`rice-day-${r.date}`}>
              <td className="p-2 font-mono text-xs">{dmy(r.date)}</td>
              <td className="p-2 text-right font-mono">{r.in > 0 ? kg3(r.in) : "—"}</td>
              <td className="p-2 text-xs text-ink-700">
                {r.inReceipts.map((rr) => (
                  <span key={rr.id}
                    className={`mr-1 inline-block rounded px-1.5 py-0.5 ${rr.legacy ? "bg-amber-100 text-amber-900" : "bg-moss/10 text-moss"}`}
                  >
                    {rr.source || "FCI"}{rr.refNo ? ` #${rr.refNo}` : ""} · {kg3(rr.kg)} kg{rr.legacy ? " · legacy" : ""}
                  </span>
                ))}
              </td>
              <td className="p-2 text-right font-mono">{r.out > 0 ? kg3(r.out) : "—"}</td>
              <td className="p-2 text-right font-mono" data-testid={`rice-day-dr-${r.date}`}>{r.drOut > 0 ? kg3(r.drOut) : "—"}</td>
              <td className={`p-2 text-right font-mono font-semibold ${r.running < 0 ? "text-rose-600" : "text-ink-900"}`}>{kg3(r.running)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-sm text-ink-700" data-testid="rice-daily-empty">No activity in this month yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function WeeklyTable({ rows }) {
  return (
    <section className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="rice-weekly-table">
      <table className="w-full text-sm">
        <thead className="bg-sand-100 text-left text-ink-700">
          <tr>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">ISO Week</th>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">From</th>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">To</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">In (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Out (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Closing at end</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-border" data-testid={`rice-week-${r.key}`}>
              <td className="p-2 font-mono text-xs">{r.key}</td>
              <td className="p-2 font-mono text-xs">{dmy(r.firstDate)}</td>
              <td className="p-2 font-mono text-xs">{dmy(r.lastDate)}</td>
              <td className="p-2 text-right font-mono">{kg3(r.in)}</td>
              <td className="p-2 text-right font-mono">{kg3(r.out)}</td>
              <td className="p-2 text-right font-mono font-semibold">{kg3(r.closingAtEnd || 0)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-sm text-ink-700">No weekly activity yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function MonthlyTable({ rows }) {
  return (
    <section className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="rice-monthly-table">
      <table className="w-full text-sm">
        <thead className="bg-sand-100 text-left text-ink-700">
          <tr>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">Month</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Opening (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">In (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Out (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">of which DR (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Closing (kg)</th>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">Legacy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.year}-${r.month}`} className="border-t border-border" data-testid={`rice-mo-${r.year}-${r.month}`}>
              <td className="p-2 font-mono text-xs">{MONTH_OPTIONS.find((m) => m.v === r.month)?.en || r.month} {r.year}</td>
              <td className="p-2 text-right font-mono">{kg3(r.opening)}</td>
              <td className="p-2 text-right font-mono">{kg3(r.in)}</td>
              <td className="p-2 text-right font-mono">{kg3(r.out)}</td>
              <td className="p-2 text-right font-mono" data-testid={`rice-mo-dr-${r.year}-${r.month}`}>{kg3(r.drOut || 0)}</td>
              <td className={`p-2 text-right font-mono font-semibold ${r.closing < 0 ? "text-rose-600" : "text-ink-900"}`}>{kg3(r.closing)}</td>
              <td className="p-2 text-xs">{r.hasLegacy ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">lump-sum</span> : "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="p-6 text-center text-sm text-ink-700">No fund records yet in this 12-month window.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function YearlyTable({ rows }) {
  return (
    <section className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="rice-yearly-table">
      <table className="w-full text-sm">
        <thead className="bg-sand-100 text-left text-ink-700">
          <tr>
            <th className="p-2 text-xs font-semibold uppercase tracking-wider">Financial Year</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Total In (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Total Out (kg)</th>
            <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">Closing at FY end</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.fy} className="border-t border-border" data-testid={`rice-fy-${r.fy}`}>
              <td className="p-2 font-mono text-xs">{r.fy}</td>
              <td className="p-2 text-right font-mono">{kg3(r.in)}</td>
              <td className="p-2 text-right font-mono">{kg3(r.out)}</td>
              <td className={`p-2 text-right font-mono font-semibold ${r.lastClosing < 0 ? "text-rose-600" : "text-ink-900"}`}>{kg3(r.lastClosing || 0)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="p-6 text-center text-sm text-ink-700">No yearly totals yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
